<template>
  <div class="system-info-container p-4 md:p-6">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">Informasi Sistem</h1>
      <p class="text-gray-600">Monitor status dan kelola sistem aplikasi</p>
    </div>

    <!-- System Status Overview Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <!-- Version Card -->
      <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200 hover:shadow-lg transition-all duration-300">
        <div class="flex items-start justify-between mb-3">
          <div class="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
            <i class="el-icon-box text-white text-xl"></i>
          </div>
          <!-- <el-tag size="mini" type="info">v{{ systemInfo.version }}</el-tag> -->
        </div>
        <h3 class="text-sm font-medium text-gray-600 mb-1">Versi Aplikasi</h3>
        <p class="text-1xl font-bold text-gray-800">{{ systemInfo.version }}</p>
      </div>

      <!-- Environment Card -->
      <div class="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200 hover:shadow-lg transition-all duration-300">
        <div class="flex items-start justify-between mb-3">
          <div class="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
            <i class="el-icon-cpu text-white text-xl"></i>
          </div>
          <!-- <el-tag :type="systemInfo.environment === 'production' ? 'success' : 'warning'" size="mini">
            {{ systemInfo.environment }}
          </el-tag> -->
        </div>
        <h3 class="text-sm font-medium text-gray-600 mb-1">Environment</h3>
        <p class="text-1xl font-bold text-gray-800 capitalize">{{ systemInfo.environment }}</p>
      </div>

      <!-- Database Card -->
      <div class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-5 border border-purple-200 hover:shadow-lg transition-all duration-300">
        <div class="flex items-start justify-between mb-3">
          <div class="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
            <i class="el-icon-coin text-white text-xl"></i>
          </div>
          <!-- <el-tag :type="systemInfo.databaseStatus === 'connected' ? 'success' : 'danger'" size="mini">
            {{ systemInfo.databaseStatus }}
          </el-tag> -->
        </div>
        <h3 class="text-sm font-medium text-gray-600 mb-1">Database</h3>
        <p class="text-1xl font-bold text-gray-800 capitalize">{{ systemInfo.databaseStatus }}</p>
      </div>

      <!-- Cache Card -->
      <div class="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-5 border border-orange-200 hover:shadow-lg transition-all duration-300">
        <div class="flex items-start justify-between mb-3">
          <div class="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center">
            <i class="el-icon-folder-opened text-white text-xl"></i>
          </div>
          <!-- <el-tag :type="systemInfo.cacheStatus === 'active' ? 'success' : 'info'" size="mini">
            {{ systemInfo.cacheStatus }}
          </el-tag> -->
        </div>
        <h3 class="text-sm font-medium text-gray-600 mb-1">Cache</h3>
        <p class="text-1xl font-bold text-gray-800 capitalize">{{ systemInfo.cacheStatus }}</p>
      </div>
    </div>

    <!-- Detailed Information -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- System Details -->
      <div class="bg-white rounded-lg shadow-lg p-6">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <i class="el-icon-info text-blue-600 text-lg"></i>
          </div>
          <h2 class="text-lg font-semibold text-gray-800">Detail Sistem</h2>
        </div>
        
        <el-descriptions :column="1" border size="medium">
          <el-descriptions-item>
            <template slot="label">
              <div class="flex items-center gap-2">
                <i class="el-icon-box text-blue-600"></i>
                <span>Versi Aplikasi</span>
              </div>
            </template>
            <span class="font-semibold">{{ systemInfo.version }}</span>
          </el-descriptions-item>
          
          <el-descriptions-item>
            <template slot="label">
              <div class="flex items-center gap-2">
                <i class="el-icon-cpu text-green-600"></i>
                <span>Environment</span>
              </div>
            </template>
            <el-tag :type="systemInfo.environment === 'production' ? 'success' : 'warning'" size="small">
              {{ systemInfo.environment }}
            </el-tag>
          </el-descriptions-item>
          
          <el-descriptions-item>
            <template slot="label">
              <div class="flex items-center gap-2">
                <i class="el-icon-coin text-purple-600"></i>
                <span>Database Status</span>
              </div>
            </template>
            <el-tag :type="systemInfo.databaseStatus === 'connected' ? 'success' : 'danger'" size="small">
              {{ systemInfo.databaseStatus }}
            </el-tag>
          </el-descriptions-item>
          
          <el-descriptions-item>
            <template slot="label">
              <div class="flex items-center gap-2">
                <i class="el-icon-folder-opened text-orange-600"></i>
                <span>Cache Status</span>
              </div>
            </template>
            <el-tag :type="systemInfo.cacheStatus === 'active' ? 'success' : 'info'" size="small">
              {{ systemInfo.cacheStatus }}
            </el-tag>
          </el-descriptions-item>
          
          <el-descriptions-item>
            <template slot="label">
              <div class="flex items-center gap-2">
                <i class="el-icon-time text-gray-600"></i>
                <span>Last Updated</span>
              </div>
            </template>
            {{ systemInfo.lastUpdated }}
          </el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- System Actions -->
      <div class="bg-white rounded-lg shadow-lg p-6">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <i class="el-icon-setting text-orange-600 text-lg"></i>
          </div>
          <h2 class="text-lg font-semibold text-gray-800">Aksi Sistem</h2>
        </div>

        <!-- Clear Cache Action -->
        <div class="mb-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
          <div class="flex items-start gap-4">
            <div class="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <i class="el-icon-refresh text-white text-xl"></i>
            </div>
            <div class="flex-1">
              <h3 class="font-semibold text-gray-800 mb-2">Clear Cache</h3>
              <p class="text-sm text-gray-600 mb-3">Hapus semua cache aplikasi untuk membebaskan memori dan memperbarui data</p>
              <el-button 
                type="warning" 
                icon="el-icon-refresh" 
                @click="clearCache" 
                :loading="clearing.cache"
                size="small"
              >
                Clear Cache
              </el-button>
            </div>
          </div>
        </div>

        <!-- Clear Logs Action -->
        <div class="p-4 bg-red-50 rounded-lg border border-red-200">
          <div class="flex items-start gap-4">
            <div class="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <i class="el-icon-delete text-white text-xl"></i>
            </div>
            <div class="flex-1">
              <h3 class="font-semibold text-gray-800 mb-2">Clear Logs</h3>
              <p class="text-sm text-gray-600 mb-3">Hapus semua file log lama untuk menghemat ruang penyimpanan</p>
              <el-button 
                type="danger" 
                icon="el-icon-delete" 
                @click="clearLogs" 
                :loading="clearing.logs"
                size="small"
              >
                Clear Logs
              </el-button>
            </div>
          </div>
        </div>

        <!-- Warning Notice -->
        <div class="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
          <div class="flex items-start gap-2">
            <i class="el-icon-warning text-yellow-600 mt-0.5"></i>
            <p class="text-xs text-gray-600">
              <strong>Perhatian:</strong> Tindakan ini akan membersihkan data sementara. Pastikan tidak ada proses penting yang sedang berjalan.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Additional System Info -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
      <!-- Uptime -->
      <div class="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-5 text-white">
        <div class="flex items-center gap-3 mb-2">
          <i class="el-icon-clock text-2xl"></i>
          <h3 class="font-semibold">System Uptime</h3>
        </div>
        <p class="text-2xl font-bold">99.9%</p>
        <p class="text-sm opacity-90 mt-1">Last 30 days</p>
      </div>

      <!-- Server Load -->
      <div class="bg-gradient-to-r from-green-500 to-teal-600 rounded-lg p-5 text-white">
        <div class="flex items-center gap-3 mb-2">
          <i class="el-icon-data-analysis text-2xl"></i>
          <h3 class="font-semibold">Server Load</h3>
        </div>
        <p class="text-2xl font-bold">45%</p>
        <p class="text-sm opacity-90 mt-1">CPU Usage</p>
      </div>

      <!-- Storage -->
      <div class="bg-gradient-to-r from-pink-500 to-rose-600 rounded-lg p-5 text-white">
        <div class="flex items-center gap-3 mb-2">
          <i class="el-icon-files text-2xl"></i>
          <h3 class="font-semibold">Storage</h3>
        </div>
        <p class="text-2xl font-bold">12.5 GB</p>
        <p class="text-sm opacity-90 mt-1">Used of 50 GB</p>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: "InformasiSistemView",
  data() {
    return {
      // System Information
      systemInfo: {
        version: '1.0.0',
        environment: 'development',
        databaseStatus: 'connected',
        cacheStatus: 'active',
        lastUpdated: new Date().toLocaleString('id-ID')
      },
      clearing: {
        cache: false,
        logs: false
      }
    }
  },
  methods: {
    async clearCache() {
      this.clearing.cache = true
      try {
        await this.$confirm('Apakah Anda yakin ingin menghapus cache?', 'Konfirmasi', {
          confirmButtonText: 'Ya, Hapus',
          cancelButtonText: 'Batal',
          type: 'warning'
        })
        
        // Implement API call to clear cache
        await new Promise(resolve => setTimeout(resolve, 1000))
        this.$message.success('Cache berhasil dihapus!')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error clearing cache:', error)
          this.$message.error('Gagal menghapus cache')
        }
      } finally {
        this.clearing.cache = false
      }
    },
    
    async clearLogs() {
      this.clearing.logs = true
      try {
        await this.$confirm('Apakah Anda yakin ingin menghapus semua logs?', 'Konfirmasi', {
          confirmButtonText: 'Ya, Hapus',
          cancelButtonText: 'Batal',
          type: 'warning'
        })
        
        // Implement API call to clear logs
        await new Promise(resolve => setTimeout(resolve, 1000))
        this.$message.success('Logs berhasil dihapus!')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error clearing logs:', error)
          this.$message.error('Gagal menghapus logs')
        }
      } finally {
        this.clearing.logs = false
      }
    }
  }
};
</script>

<style scoped>
.system-info-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
}

/* Card Hover Effects */
.hover\:shadow-lg:hover {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

/* Icon Animation */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.bg-white:hover .w-10 {
  animation: pulse 2s ease-in-out infinite;
}

/* Gradient Cards Animation */
.bg-gradient-to-r {
  background-size: 200% auto;
  animation: gradient-shift 3s ease infinite;
}

@keyframes gradient-shift {
  0%, 100% {
    background-position: 0% center;
  }
  50% {
    background-position: 100% center;
  }
}

/* Button Hover */
.el-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

/* Status Badge Colors */
.text-blue-600 {
  color: #409EFF;
}

.text-green-600 {
  color: #67C23A;
}

.text-purple-600 {
  color: #9b59b6;
}

.text-orange-600 {
  color: #E6A23C;
}

.text-gray-600 {
  color: #909399;
}

.text-yellow-600 {
  color: #F56C6C;
}

/* Responsive */
@media (max-width: 768px) {
  .grid-cols-4 {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
  
  .lg\:grid-cols-2 {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>